import { AddIcon, MinusIcon } from "@chakra-ui/icons"
import type { ChakraProps } from "@chakra-ui/react"
import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  Heading,
  Text,
} from "@chakra-ui/react"
import { withErrorBoundary } from "react-error-boundary"
import { Logger } from "lib/logger"
import { useInView } from "react-intersection-observer"
import { trackProductDescriptionViewed } from "utils/gtm"
import type { ReactNode } from "react"
import React, { useState, useEffect, useCallback } from "react"
import { showCandleSafety } from "utils/show-candle-safety"
import { useSegment } from "tracking"
import { PDPBodySection } from "components/Shop/PDPPage/PDPBodySection"
import { KeyIngredients } from "components/Shop/PDPPage/KeyIngredients"
import { Standards } from "components/Shop/PDPPage/PDPHeader/Standards"
import { Footnotes } from "components/Shop/PDPPage/Footnotes"
import { SizeGuide } from "components/Shop/PDPPage/SizeGuide"
import { DetailsAndCare } from "components/Shop/PDPPage/DetailsAndCare"
import { KeyBenefits } from "components/Shop/PDPPage/KeyBenefits"
import { FormulatedWith } from "components/Shop/PDPPage/FormulatedWith"
import { RecommendedFor } from "components/Shop/PDPPage/RecommendedFor"
import { CList } from "components/Shop/PDPPage/CList"
import { CandleSafety } from "components/Shop/PDPPage/CandleSafety"
import type { VariantType } from "types"
import { Highlights } from "../Highlights"
import { useNMCustomerJourney } from "packages/acquisition/components/GuestPrice/hooks/useNMCustomerJourney"

type BaseProps = ChakraProps & {
  variant: VariantType
}

type Props = BaseProps & {
  listItems: {
    label: string
    id?: string
  }[]
}

export const getAccordionItem = (
  children: ReactNode,
  title: string,
  id?: string
): ReactNode => {
  const expandedProps = { variant: "acuminRegular" }

  return (
    <AccordionItem sx={{ "&:first-of-type": { borderTop: "0" } }}>
      {({ isExpanded }) => {
        const collapsedProps = { variant: "acuminMedium" }

        return (
          <Box
            id={id ?? undefined}
            scrollMarginTop={{ base: "110px", lg: "150px" }}
          >
            <AccordionButton
              pr={2}
              pl={0}
              py={{ base: isExpanded ? 0 : -4, md: isExpanded ? 0 : 1 }}
              minH="3.5rem"
            >
              <Box flex="1" textAlign="left" pl={-1}>
                {isExpanded ? (
                  <Heading as="h3" size="lg" {...expandedProps}>
                    {title}
                  </Heading>
                ) : (
                  <Text mt={0} {...collapsedProps}>
                    {title}
                  </Text>
                )}
              </Box>
              {isExpanded ? (
                <MinusIcon fontSize="12px" />
              ) : (
                <AddIcon fontSize="12px" />
              )}
            </AccordionButton>
            <AccordionPanel px={0} pb={3} pt={1}>
              {children}
            </AccordionPanel>
          </Box>
        )
      }}
    </AccordionItem>
  )
}

export const HighlightsSection: React.FC<BaseProps> = ({ variant }) => {
  const categoryName = variant?.categoryName
  const hasHighlights = !!variant?.highlights?.length
  const hasKeyIngredients = !!variant?.formula?.keyIngredients?.length
  const hasFootnotes = !!variant.footNotes?.length
  const hasHighlightSection = hasHighlights || hasKeyIngredients || hasFootnotes

  const headingProps = {
    variant: "apercu",
    sx: {
      display: "none",
    },
  }

  // handling a Commercetools special case where keyBenefits come from variant.formula
  const ctSupplementSpecialCase =
    categoryName === "Beauty Supplements" && hasKeyIngredients

  return (
    <>
      {hasHighlightSection &&
        getAccordionItem(
          <>
            {hasHighlights && variant?.highlights && (
              <PDPBodySection
                title="Highlights"
                headingProps={headingProps}
                mb={hasKeyIngredients || hasFootnotes ? 1 : 0}
                mt="-15px"
              >
                <Highlights highlights={variant?.highlights} />
              </PDPBodySection>
            )}
            {!ctSupplementSpecialCase && hasKeyIngredients && (
              <PDPBodySection
                title=""
                customHeading={
                  <Text as="h2" variant="acuminRegular" mt={0} mb={1}>
                    Key Benefit by Ingredient
                  </Text>
                }
                mb={hasFootnotes ? 1 : 0}
              >
                <KeyIngredients
                  keyIngredients={variant.formula!.keyIngredients!}
                />
              </PDPBodySection>
            )}
            {hasFootnotes && (
              <Footnotes footNotes={variant.footNotes as string[]} />
            )}
          </>,
          "Highlights",
          "about-this-product"
        )}
    </>
  )
}

const PDPAccordionLayoutComponent: React.FC<Props> = ({
  variant,
  listItems,
  ...otherProps
}) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0,
  })
  const [index, setIndex] = useState(0)
  const [accordion, setAccordion] = useState([0])
  const hasStandards = !!variant.standards
  const hasFootnotes = !!variant.footNotes?.length
  const { categoryName } = variant
  const hasKeyIngredients = !!variant?.formula?.keyIngredients
  // handling a Commercetools special case where keyBenefits come from variant.formula
  const ctSupplementSpecialCase =
    categoryName === "Beauty Supplements" && hasKeyIngredients
  const isSize = variant?.type === "size"
  const hasLifestyleAsPrimaryCategory = variant.categoryName === "Lifestyle"
  const isNMCustomerJourney = useNMCustomerJourney()

  const isSizeTypeAndLifestyleCategory = isSize && hasLifestyleAsPrimaryCategory
  const headingProps = {
    sx: {
      display: "none",
    },
    ...{
      variant: "acuminLight",
    },
  }

  const { track } = useSegment()

  useEffect(() => {
    if (inView) {
      trackProductDescriptionViewed(listItems[index].label)
      track({
        event: "Page Element Viewed",
        properties: {
          module_content: "PDP Description",
          module_content_details: listItems[index].label,
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listItems, inView, index])

  useEffect(() => {
    const focusHighlights = () => {
      if (window.location.hash === "#about-this-product") {
        setTimeout(() => {
          setAccordion([...accordion, 1])
          setIndex(1)
        }, 500)
      }
    }

    window.addEventListener("hashchange", focusHighlights)
    return () => {
      window.removeEventListener("hashchange", focusHighlights)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isNMCustomerJourney) {
      setAccordion([1])
    }
  }, [isNMCustomerJourney])

  const onAccordionChange = useCallback(
    (state: Array<number>) => {
      if (state.length > accordion.length) {
        setIndex(state[state.length - 1])
      }
      setAccordion(state)
    },
    [accordion, setIndex, setAccordion]
  )

  return (
    <Accordion
      ref={ref}
      data-testid="pdp-accordion"
      className="pdp-accordion"
      index={accordion}
      allowMultiple
      w="full"
      onChange={onAccordionChange}
      pb={{ base: 5, lg: "80px" }}
      {...otherProps}
    >
      {getAccordionItem(
        <>
          {hasStandards && variant.standards && (
            <Standards
              standards={variant.standards}
              mb={hasFootnotes ? 1 : 0}
              pricing={variant.pricing}
            />
          )}
        </>,
        "About this product"
      )}

      <HighlightsSection variant={variant} />

      {isSizeTypeAndLifestyleCategory &&
        getAccordionItem(
          <PDPBodySection title="Size and fit" headingProps={headingProps}>
            <SizeGuide productTitle={variant.title} />
          </PDPBodySection>,
          "Size and fit",
          "sizeAccordion"
        )}

      {isSizeTypeAndLifestyleCategory &&
        getAccordionItem(
          <PDPBodySection title="Details and care" headingProps={headingProps}>
            <DetailsAndCare productTitle={variant.title} />
          </PDPBodySection>,
          "Details and care",
          "detailsAndCare"
        )}

      {!!variant?.keyBenefits?.length &&
        !ctSupplementSpecialCase &&
        getAccordionItem(
          <>
            <KeyBenefits keyBenefits={variant?.keyBenefits} />
          </>,
          "Key benefit by ingredient"
        )}

      {ctSupplementSpecialCase &&
        getAccordionItem(
          <PDPBodySection
            title="Key benefit by ingredient"
            headingProps={headingProps}
            mb={hasStandards || hasFootnotes ? 3 : 0}
          >
            <KeyIngredients keyIngredients={variant.formula!.keyIngredients!} />
          </PDPBodySection>,
          "Key benefit by ingredient"
        )}

      {!!variant?.formula?.ingredients?.length &&
        getAccordionItem(
          <PDPBodySection
            title="Full ingredients list"
            headingProps={headingProps}
          >
            <FormulatedWith variant={variant} />
          </PDPBodySection>,
          "Full Ingredients List"
        )}

      {variant?.recommendedFor &&
        getAccordionItem(
          <PDPBodySection title="Recommended for" headingProps={headingProps}>
            <RecommendedFor recommendedFor={variant?.recommendedFor} />
          </PDPBodySection>,
          "Recommended for"
        )}

      {variant?.cListRecommended &&
        getAccordionItem(
          <PDPBodySection
            title="The C-List recommended product"
            headingProps={headingProps}
          >
            <CList />
          </PDPBodySection>,
          "The C-List recommended product"
        )}

      {showCandleSafety(variant) &&
        getAccordionItem(
          <PDPBodySection title="Candle Safety" headingProps={headingProps}>
            <CandleSafety />
          </PDPBodySection>,
          "Candle safety"
        )}
    </Accordion>
  )
}

export const PDPAccordionLayoutB = withErrorBoundary(
  PDPAccordionLayoutComponent,
  {
    fallback: <div />,
    onError(error) {
      Logger.warn(`PDPAccordionLayoutB failed to load. ${error}`)
    },
  }
)
